import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { RANGO_MAXIMO_DIAS } from '@adelante/shared';
import {
  addDays,
  addMonths,
  differenceInDays,
  endOfMonth,
  format,
  isSameDay,
  startOfMonth,
  subMonths,
} from 'date-fns';
import { es } from 'date-fns/locale';
import { CalendarIcon, ChevronLeft, ChevronRight } from 'lucide-react';
import { useEffect, useState } from 'react';
import type { DateRange } from 'react-day-picker';

export interface RangoFechas {
  /** YYYY-MM-DD */
  desde: string;
  /** YYYY-MM-DD */
  hasta: string;
}

interface Props {
  valor: RangoFechas;
  onChange: (rango: RangoFechas) => void;
}

type Preset = 'mes_actual' | 'mes_anterior' | 'ultimos_30' | 'personalizado';

const PRESETS: { id: Preset; label: string }[] = [
  { id: 'mes_actual', label: 'Mes actual' },
  { id: 'mes_anterior', label: 'Mes anterior' },
  { id: 'ultimos_30', label: 'Últimos 30 días' },
  { id: 'personalizado', label: 'Personalizado…' },
];

function aIso(d: Date): string {
  return format(d, 'yyyy-MM-dd');
}

function aDate(iso: string): Date {
  // Construimos la fecha en zona horaria LOCAL (no UTC).
  //
  // Sutil pero crítico: si usás `new Date('2025-09-01T00:00:00Z')` y estás en
  // CR (UTC-6), el Date representa "31 ago 18:00 local". Después date-fns
  // (`startOfMonth`, `format`, etc.) opera en local y devuelve "1 agosto"
  // en vez de "1 septiembre". Eso rompe la navegación mes-a-mes y muestra
  // el mes anterior en el label.
  //
  // Al construir con `new Date(y, m-1, d)` el Date queda exactamente en
  // "1 sep 00:00 local", consistente con todo el resto del código.
  const partes = iso.split('-').map(Number);
  const year = partes[0] ?? 1970;
  const month = partes[1] ?? 1;
  const day = partes[2] ?? 1;
  return new Date(year, month - 1, day);
}

export function calcularRangoPreset(preset: Exclude<Preset, 'personalizado'>): RangoFechas {
  const hoy = new Date();
  switch (preset) {
    case 'mes_actual':
      return { desde: aIso(startOfMonth(hoy)), hasta: aIso(endOfMonth(hoy)) };
    case 'mes_anterior': {
      const mesAnt = subMonths(hoy, 1);
      return { desde: aIso(startOfMonth(mesAnt)), hasta: aIso(endOfMonth(mesAnt)) };
    }
    case 'ultimos_30':
      return { desde: aIso(addDays(hoy, -29)), hasta: aIso(hoy) };
  }
}

/**
 * Devuelve true si el rango representa exactamente un mes calendario
 * completo (desde = día 1, hasta = último día del mismo mes).
 */
function esMesCompleto({ desde, hasta }: RangoFechas): boolean {
  const dDesde = aDate(desde);
  const dHasta = aDate(hasta);
  return isSameDay(dDesde, startOfMonth(dDesde)) && isSameDay(dHasta, endOfMonth(dDesde));
}

/**
 * Detecta qué preset matchea el rango actual (para mantener el select
 * sincronizado cuando el usuario navega con las flechas o cambia las
 * fechas manualmente).
 */
function detectarPreset(rango: RangoFechas): Preset {
  for (const p of ['mes_actual', 'mes_anterior', 'ultimos_30'] as const) {
    const r = calcularRangoPreset(p);
    if (r.desde === rango.desde && r.hasta === rango.hasta) return p;
  }
  return 'personalizado';
}

function formatearRango(rango: RangoFechas): string {
  // Si es un mes calendario completo, mostramos el nombre del mes
  // (más limpio para navegación mes a mes). En cualquier otro caso,
  // mostramos el rango día-a-día.
  if (esMesCompleto(rango)) {
    return format(aDate(rango.desde), 'MMMM yyyy', { locale: es }).replace(/^./, (c) =>
      c.toUpperCase(),
    );
  }
  const formato = 'd MMM yyyy';
  return `${format(aDate(rango.desde), formato, { locale: es })} – ${format(aDate(rango.hasta), formato, { locale: es })}`;
}

/**
 * Devuelve el rango del mes calendario adyacente al `desde` actual.
 * `direccion` = -1 para mes anterior, +1 para mes siguiente.
 *
 * Si el rango actual NO es un mes completo, igual usamos el mes del `desde`
 * como pivote y nos movemos desde ahí (UX predecible).
 */
function rangoMesAdyacente(rango: RangoFechas, direccion: -1 | 1): RangoFechas {
  const pivote = startOfMonth(aDate(rango.desde));
  const nuevoMes = addMonths(pivote, direccion);
  return { desde: aIso(startOfMonth(nuevoMes)), hasta: aIso(endOfMonth(nuevoMes)) };
}

export function FiltroPeriodo({ valor, onChange }: Props) {
  const [preset, setPreset] = useState<Preset>(() => detectarPreset(valor));
  const [popoverAbierto, setPopoverAbierto] = useState(false);
  const [rangoTemp, setRangoTemp] = useState<DateRange | undefined>({
    from: aDate(valor.desde),
    to: aDate(valor.hasta),
  });

  // Sincroniza el preset cuando el rango cambia desde afuera (p.ej. las
  // flechas pueden venir de un re-render con un valor nuevo, o el padre
  // resetea el rango).
  useEffect(() => {
    setPreset(detectarPreset(valor));
  }, [valor]);

  const handlePresetChange = (nuevo: Preset) => {
    setPreset(nuevo);
    if (nuevo !== 'personalizado') {
      onChange(calcularRangoPreset(nuevo));
    } else {
      setPopoverAbierto(true);
    }
  };

  const handleNavegarMes = (direccion: -1 | 1) => {
    onChange(rangoMesAdyacente(valor, direccion));
    // El useEffect de arriba se encarga de actualizar el preset.
  };

  const diasRango = differenceInDays(aDate(valor.hasta), aDate(valor.desde));
  const excedeMaximo = diasRango > RANGO_MAXIMO_DIAS;

  const aplicarRangoTemp = () => {
    if (!rangoTemp?.from || !rangoTemp?.to) return;
    onChange({ desde: aIso(rangoTemp.from), hasta: aIso(rangoTemp.to) });
    setPopoverAbierto(false);
  };

  return (
    <div className="flex flex-wrap items-center gap-2">
      <Select value={preset} onValueChange={(v) => handlePresetChange(v as Preset)}>
        <SelectTrigger className="w-48">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {PRESETS.map((p) => (
            <SelectItem key={p.id} value={p.id}>
              {p.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {/* Flechas de navegación mes-a-mes. Siempre visibles porque son útiles
          en cualquier preset (al click, switchean al mes calendario navegado
          y el preset se ajusta solo vía useEffect). */}
      <div className="flex items-center gap-1">
        <Button
          variant="outline"
          size="icon"
          onClick={() => handleNavegarMes(-1)}
          aria-label="Mes anterior"
          title="Mes anterior"
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <Button
          variant="outline"
          size="icon"
          onClick={() => handleNavegarMes(1)}
          aria-label="Mes siguiente"
          title="Mes siguiente"
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>

      {preset === 'personalizado' && !esMesCompleto(valor) && (
        <Popover open={popoverAbierto} onOpenChange={setPopoverAbierto}>
          <PopoverTrigger asChild>
            <Button variant="outline" className="w-72 justify-start text-left font-normal">
              <CalendarIcon className="mr-2 h-4 w-4" />
              {formatearRango(valor)}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <Calendar
              mode="range"
              defaultMonth={rangoTemp?.from ?? new Date()}
              selected={rangoTemp}
              onSelect={setRangoTemp}
              numberOfMonths={2}
              locale={es}
            />
            <div className="flex items-center justify-end gap-2 border-t p-2">
              <Button variant="ghost" size="sm" onClick={() => setPopoverAbierto(false)}>
                Cancelar
              </Button>
              <Button
                size="sm"
                onClick={aplicarRangoTemp}
                disabled={!rangoTemp?.from || !rangoTemp?.to}
              >
                Aplicar
              </Button>
            </div>
          </PopoverContent>
        </Popover>
      )}

      {(preset !== 'personalizado' || esMesCompleto(valor)) && (
        <span className="text-sm text-muted-foreground">{formatearRango(valor)}</span>
      )}

      {excedeMaximo && (
        <span className="text-sm font-medium text-destructive">
          ⚠️ Rango excede {RANGO_MAXIMO_DIAS} días — los KPIs no se cargarán
        </span>
      )}
    </div>
  );
}
